import { type FilterFunction } from '@react-three/fiber/dist/declarations/src/core/events'
import { useCallback } from 'react'
import { Intersection, Object3D, Object3DEventMap } from 'three'
import { CLICK_PRIORITY } from '~/config/clickPriority'
import LayerKeys from '~/config/layerKeys'
import { useAppSelector } from '~/store/hooks'

const isLayerKey = (key: string): key is LayerKeys => key in LayerKeys

const getObjectLayerKey = (object: Object3D): LayerKeys | '' => {
  let target: null | Object3D<Object3DEventMap> = object
  while (target) {
    if (isLayerKey(target.name)) return target.name
    target = target.parent
  }
  return ''
}

export const useReorderIntersections = (): FilterFunction => {
  const layers = useAppSelector(state => state.layers.layers)
  return useCallback(
    (items, _state) => {
      const filtered = items.filter(
        (intersection): intersection is Intersection<Object3D & { userData: { layer: LayerKeys } }> => {
          const { object } = intersection
          const layerKey = getObjectLayerKey(object)
          object.userData = { layer: layerKey }
          return !!layerKey && !layers[layerKey].locked && layers[layerKey].visible
        }
      )
      filtered.sort((a, b) => {
        const aPriority = CLICK_PRIORITY[a.object.userData.layer] ?? Infinity
        const bPriority = CLICK_PRIORITY[b.object.userData.layer] ?? Infinity
        return aPriority - bPriority || a.distance - b.distance
      })
      return filtered
    },
    [layers]
  )
}
